import {
  Collapse,
  createStyles,
  Grid,
  IconButton,
  makeStyles,
  Radio,
  Theme,
  Tooltip,
  Typography,
} from '@material-ui/core';
import {
  ArrowForward,
  Cancel,
  CheckCircle,
  Error,
  KeyboardArrowDown,
  KeyboardArrowUp,
  WatchLater,
} from '@material-ui/icons';
import { statusFormularioDescriptions } from 'common/genericDescriptions';
import { RegistroStatus } from 'common/genericTypes';
import { SmSpace, TextButton } from 'm2m.components';
import React, { useState } from 'react';
import { CredenciadosResponse } from 'store/modules/credenciados/credenciadosReponse';

const useStyles = (status: RegistroStatus) =>
  makeStyles((theme: Theme) => {
    const colors: { [key: string]: string } = {
      [RegistroStatus.EmAnalise]: theme.palette.warning.main,
      [RegistroStatus.Aprovado]: theme.palette.primary.main,
      [RegistroStatus.Reprovado]: theme.palette.error.main,
      [RegistroStatus.AguardandoCorrecao]: theme.palette.warning.main,
      [RegistroStatus.Excluido]: theme.palette.error.main,
      [RegistroStatus.AguardandoAprovacaoEdicao]: theme.palette.warning.main,
      [RegistroStatus.AguardandoAprovacaoAditamento]: theme.palette.warning.main,
    };
    return createStyles({
      icon: {
        color: colors[status] ?? theme.palette.grey,
        marginRight: 10,
      },
      wrapTxt: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      },
      textButton: {
        padding: '0 0 0 0',
        marginRight: '10px',
        borderBottom: `1px solid ${theme.palette.primary.main}`,
        borderRadius: 0,
      },
    });
  });

interface CredenciadoListItemProps {
  credenciado: CredenciadosResponse;
  checked: boolean;
  onRadioCheck?: (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => void;
  onDetailsEmissorButtonClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onDetailsCredenciadoButtonClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  onMainButtonClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  mainButtonDescription?: string;
  detailsButtonDescription?: string;
  disabled?: boolean;
  documentOpen: boolean;
  onDocumentClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

type Props = CredenciadoListItemProps;

export const CredenciadoListItem: React.FC<Props> = (props) => {
  const {
    credenciado,
    checked,
    detailsButtonDescription,
    documentOpen,
    onDetailsEmissorButtonClick,
    onDetailsCredenciadoButtonClick,
    onRadioCheck,
    onMainButtonClick,
    mainButtonDescription,
    onDocumentClick,
  } = props;
  const classes = useStyles(credenciado.status)();
  const [openDetails, setOpenDetails] = useState<boolean>(false);
  const getStatusIcon = (status: RegistroStatus) => {
    switch (status) {
      case RegistroStatus.EmAnalise:
        return <WatchLater fontSize="small" className={classes.icon} />;
      case RegistroStatus.Aprovado:
        return <CheckCircle fontSize="small" className={classes.icon} />;
      case RegistroStatus.Reprovado:
        return <Cancel fontSize="small" className={classes.icon} />;
      case RegistroStatus.AguardandoCorrecao:
        return <Error fontSize="small" className={classes.icon} />;
      case RegistroStatus.Excluido:
        return <Cancel fontSize="small" className={classes.icon} />;
      case RegistroStatus.AguardandoAprovacaoEdicao:
        return <WatchLater fontSize="small" className={classes.icon} />;
      case RegistroStatus.AguardandoAprovacaoAditamento:
        return <WatchLater fontSize="small" className={classes.icon} />;
    }
  };

  return (
    <div style={{ border: '1px solid #E5E5E5', borderRadius: 8, marginBottom: 10 }}>
      <div style={{ padding: 10 }}>
        <Grid container alignItems="center" spacing={2}>
          <Grid item sm={4}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {onRadioCheck && (
                <Radio
                  checked={checked}
                  onChange={onRadioCheck}
                  value={credenciado.id}
                  color="primary"
                  disabled={props.disabled}
                />
              )}
              <Tooltip
                title={statusFormularioDescriptions[credenciado.status]}
                aria-label={statusFormularioDescriptions[credenciado.status]}
              >
                {getStatusIcon(credenciado.status)}
              </Tooltip>
              <Typography variant="body2" className={classes.wrapTxt}>
                {credenciado.credenciadoCnpj}
              </Typography>
            </div>
          </Grid>
          <Grid item sm={4}>
            <Typography className={classes.wrapTxt} variant="body2">
              {credenciado.credenciadoRazaoSocial}
            </Typography>
          </Grid>
          <Grid item sm={4}>
            <Grid container justify="space-between">
              <Typography className={classes.wrapTxt} variant="body2">
                {statusFormularioDescriptions[credenciado.status]}
              </Typography>
              {onMainButtonClick && (
                <TextButton className={classes.textButton} onClick={onMainButtonClick}>
                  {mainButtonDescription || `Ver Justificativa`}
                </TextButton>
              )}
              <IconButton aria-label="expand row" size="small" onClick={() => setOpenDetails(!openDetails)}>
                {openDetails ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
              </IconButton>
            </Grid>
          </Grid>
        </Grid>
      </div>
      <Collapse in={openDetails} timeout="auto" unmountOnExit style={{ backgroundColor: '#F6F6F9', padding: 20 }}>
        <Grid container spacing={2}>
          <Grid item sm={4}>
            <Typography variant="body1">{'CNPJ Emissor:'}</Typography>
            <Typography variant="body2">{credenciado.emissorCnpj}</Typography>
          </Grid>
          <Grid item sm={4}>
            <Typography variant="body1">{'Razão Social Emissor:'}</Typography>
            <Typography variant="body2">{credenciado.emissorRazaoSocial}</Typography>
          </Grid>
          <Grid item sm={3}></Grid>
        </Grid>
        <SmSpace />
        <Grid container spacing={2}>
          <Grid item sm={4}>
            <Typography variant="body1">{'CNPJ Credenciado:'}</Typography>
            <Typography variant="body2">{credenciado.credenciadoCnpj}</Typography>
          </Grid>
          <Grid item sm={4}>
            <Typography variant="body1">{'Razão Social Credenciado:'}</Typography>
            <Typography variant="body2">{credenciado.credenciadoRazaoSocial}</Typography>
          </Grid>
          <Grid item sm={4}>
            <Typography variant="body1">{'Status:'}</Typography>
            <Typography variant="body2">{statusFormularioDescriptions[credenciado.status]}</Typography>
          </Grid>
        </Grid>
        {onDetailsEmissorButtonClick && (
          <Grid container justify="flex-end">
            <Grid item sm={3} style={{ textAlign: 'right' }}>
              <TextButton onClick={onDetailsEmissorButtonClick}>
                {detailsButtonDescription || 'Mais Detalhes Emissor'}
                <ArrowForward style={{ marginLeft: 10 }} fontSize={'small'} />
              </TextButton>
            </Grid>
          </Grid>
        )}
        {onDetailsCredenciadoButtonClick && (
          <Grid container justify="flex-end">
            <Grid item sm={3} style={{ textAlign: 'right' }}>
              <TextButton onClick={onDetailsCredenciadoButtonClick}>
                {detailsButtonDescription || 'Mais Detalhes Credenciado'}
                <ArrowForward style={{ marginLeft: 10 }} fontSize={'small'} />
              </TextButton>
            </Grid>
          </Grid>
        )}
        {onDocumentClick && (
          <Grid container justify="flex-end">
            <Grid item sm={12} style={{ textAlign: 'left' }}>
              <TextButton onClick={onDocumentClick}>
                {documentOpen || 'Visualizar documentos'}
                <ArrowForward style={{ marginLeft: 10 }} fontSize={'small'} />
              </TextButton>
            </Grid>
          </Grid>
        )}
      </Collapse>
    </div>
  );
};
